import React, { useState } from 'react';
import { Menu, X, Globe, ShoppingCart, ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import logo from '../assets/logo3.png';                  

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const isOrderPage = location.pathname === '/order';

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FED09D]/95 backdrop-blur-sm border-b border-[#B22222]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logo} 
              alt="Griya Bakpia Logo" 
              width="80"
              height="80"
              className="w-20 h-20 object-contain" 
            />
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-[#3E2723] leading-none">Griya Bakpia</h1>
              <p className="text-sm text-[#3E2723]/70 mt-1 font-medium">Jogja</p>
            </div>
          </Link>

          {isOrderPage ? (
            <Button onClick={() => navigate('/')} className="bg-[#B22222] hover:bg-[#8B0000] text-white px-6 py-2 rounded-full transition-all duration-300 hover:shadow-lg font-bold flex items-center">
              <ArrowLeft className="mr-2" size={18} /> Beranda
            </Button>
          ) : (
            <>
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-6">
                <button onClick={() => scrollToSection('products')} className="text-[#3E2723] hover:text-[#B22222] transition-colors duration-300 font-medium">{t.nav.products}</button>
                <button onClick={() => scrollToSection('benefits')} className="text-[#3E2723] hover:text-[#B22222] transition-colors duration-300 font-medium">{t.nav.benefits}</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-[#3E2723] hover:text-[#B22222] transition-colors duration-300 font-medium">{t.nav.testimonials}</button>
                <button onClick={() => scrollToSection('contact')} className="text-[#3E2723] hover:text-[#B22222] transition-colors duration-300 font-medium">{t.nav.contact}</button>
                
                <button onClick={toggleLanguage} className="flex items-center gap-2 text-[#3E2723] hover:text-[#B22222] transition-colors duration-300">
                  <Globe size={18} />
                  <span className="text-sm font-medium">{language.toUpperCase()}</span>
                </button>

                <Link to="/order" className="relative">
                  <Button className="bg-[#B22222] hover:bg-[#8B0000] text-white px-6 py-2 rounded-full transition-all duration-300 hover:shadow-lg flex gap-2">
                    <ShoppingCart size={18} />
                    {t.nav.orderNow}
                    {getTotalItems() > 0 && (
                      <span className="bg-white text-[#B22222] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                        {getTotalItems()}
                      </span>
                    )}
                  </Button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <div className="md:hidden flex items-center gap-4">
                <Link to="/order" className="relative">
                  <ShoppingCart className="text-[#3E2723]" size={24} />
                  {getTotalItems() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#B22222] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {getTotalItems()}
                    </span>
                  )}
                </Link>
                <button onClick={() => setIsOpen(!isOpen)} className="text-[#3E2723] hover:text-[#B22222] transition-colors duration-300">
                  {isOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        {!isOrderPage && isOpen && (
          <div className="md:hidden pb-6 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('products')} className="text-[#3E2723] py-2 font-medium text-left">{t.nav.products}</button>
              <button onClick={() => scrollToSection('benefits')} className="text-[#3E2723] py-2 font-medium text-left">{t.nav.benefits}</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-[#3E2723] py-2 font-medium text-left">{t.nav.testimonials}</button>
              <button onClick={() => scrollToSection('contact')} className="text-[#3E2723] py-2 font-medium text-left">{t.nav.contact}</button>
              <button onClick={() => { toggleLanguage(); setIsOpen(false); }} className="flex items-center gap-2 text-[#3E2723] py-2 font-medium">
                <Globe size={18} /> {language === 'id' ? 'Bahasa Indonesia' : 'English'}
              </button>
              <Button onClick={() => { navigate('/order'); setIsOpen(false); }} className="bg-[#B22222] hover:bg-[#8B0000] text-white rounded-full transition-all duration-300 w-full">
                {t.nav.orderNow}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;

